import type { MountedExperiment } from '@learnforge/demo-kit';
import { createSliderRow } from '@learnforge/demo-kit';
import { parse, sampleCurve, type ExprNode } from '../func';
import { createActionBar } from '../action-bar';
import { createFunctionGraph, type FunctionGraphHandle } from '../render/function-graph';
import {
  createCartesianViewport,
  createFormulaLegend,
  createSvgEl,
  type CartesianViewportHandle,
  type CartesianViewportView,
} from '@learnforge/demo-render';
import { FAMILY_PRESETS } from '../data/family-presets';
import type { FuncContext } from '../types';

export type FamilyId =
  | 'linear'
  | 'quadratic'
  | 'cubic'
  | 'polynomial'
  | 'rational'
  | 'exponential'
  | 'logarithmic'
  | 'absolute-value'
  | 'square-root'
  | 'sin'
  | 'cos'
  | 'tan';

function n(p: Record<string, number>, k: string): number {
  return p[k] ?? 0;
}

export function familyToExpression(family: FamilyId, p: Record<string, number>): string {
  switch (family) {
    case 'linear':
      return `${n(p, 'm')}*x + ${n(p, 'b')}`;
    case 'quadratic':
      return `${n(p, 'a')}*x^2 + ${n(p, 'b')}*x + ${n(p, 'c')}`;
    case 'cubic':
      return `${n(p, 'a')}*x^3 + ${n(p, 'b')}*x^2 + ${n(p, 'c')}*x + ${n(p, 'd')}`;
    case 'polynomial':
      return [0, 1, 2, 3, 4, 5, 6].map((i) => `${n(p, `c${i}`)}*x^${i}`).join(' + ');
    case 'rational':
      return `(${n(p, 'numA')}*x + ${n(p, 'numB')}) / (${n(p, 'denA')}*x + ${n(p, 'denB')})`;
    case 'exponential':
      return `${n(p, 'a')} * (${n(p, 'b')})^x + ${n(p, 'c')}`;
    case 'logarithmic':
      return `${n(p, 'a')} * log(x, ${n(p, 'b')}) + ${n(p, 'c')}`;
    case 'absolute-value':
      return `${n(p, 'a')} * abs(x - ${n(p, 'h')}) + ${n(p, 'k')}`;
    case 'square-root':
      return `${n(p, 'a')} * sqrt(x - ${n(p, 'h')}) + ${n(p, 'k')}`;
    case 'sin':
      return 'sin(x)';
    case 'cos':
      return 'cos(x)';
    case 'tan':
      return 'tan(x)';
  }
}

export function buildFamilyEvaluator(family: FamilyId, p: Record<string, number>): ExprNode {
  return parse(familyToExpression(family, p)).node;
}

const FAMILIES: { id: FamilyId; labelKey: string }[] = [
  { id: 'linear', labelKey: 'family.linear.label' },
  { id: 'quadratic', labelKey: 'family.quadratic.label' },
  { id: 'cubic', labelKey: 'family.cubic.label' },
  { id: 'polynomial', labelKey: 'family.polynomial.label' },
  { id: 'rational', labelKey: 'family.rational.label' },
  { id: 'exponential', labelKey: 'family.exponential.label' },
  { id: 'logarithmic', labelKey: 'family.logarithmic.label' },
  { id: 'absolute-value', labelKey: 'family.absolute-value.label' },
  { id: 'square-root', labelKey: 'family.square-root.label' },
  { id: 'sin', labelKey: 'family.sin.label' },
  { id: 'cos', labelKey: 'family.cos.label' },
  { id: 'tan', labelKey: 'family.tan.label' },
];

export function mountFamilyZoo(ctx: FuncContext): MountedExperiment {
  let family: FamilyId = 'quadratic';
  let params: Record<string, number> = { ...FAMILY_PRESETS[family] };

  const svg = createSvgEl('svg', { viewBox: '0 0 800 460', class: 'fn-stage' });
  ctx.root.appendChild(svg);
  const stageG = createSvgEl('g');
  svg.appendChild(stageG);
  const legend = createFormulaLegend(ctx.root);
  let view: CartesianViewportView = { xDomain: [-10, 10], yDomain: [-10, 10], zoom: 1 };
  let viewport: CartesianViewportHandle | null = null;

  let graphHandle: FunctionGraphHandle | null = null;
  function redraw(): void {
    const node = buildFamilyEvaluator(family, params);
    const sampleCount = Math.max(320, Math.round(800 * (globalThis.devicePixelRatio || 1)));
    const sample = sampleCurve(node, view.xDomain[0], view.xDomain[1], sampleCount);
    const options = {
      xDomain: view.xDomain,
      yDomain: view.yDomain,
      width: 800,
      height: 460,
      curves: [{ sample, className: 'fn-curve-primary', label: 'f(x)' }],
      i18n: ctx.i18n,
    };
    if (graphHandle) graphHandle.update(options);
    else graphHandle = createFunctionGraph(stageG, options);
    legend.setEntries([
      {
        id: family,
        formula: `f(x) = ${familyToExpression(family, params)}`,
        color: 'var(--accent)',
      },
    ]);
  }

  const familyRow = document.createElement('div');
  familyRow.className = 'fn-panel-row fn-dropdown-row';
  const familyLabel = document.createElement('label');
  familyLabel.textContent = ctx.i18n.t('param.family.label');
  const familySelect = document.createElement('select');
  for (const f of FAMILIES) {
    const opt = document.createElement('option');
    opt.value = f.id;
    opt.textContent = ctx.i18n.t(f.labelKey as never);
    familySelect.appendChild(opt);
  }
  familySelect.value = family;
  familySelect.addEventListener('change', () => {
    family = familySelect.value as FamilyId;
    params = { ...FAMILY_PRESETS[family] };
    renderParamSliders();
    viewport?.setBase({ xDomain: [-10, 10], yDomain: [-10, 10] });
  });
  familyRow.appendChild(familyLabel);
  familyRow.appendChild(familySelect);
  ctx.panel.appendChild(familyRow);

  const paramSlidersHost = document.createElement('div');
  ctx.panel.appendChild(paramSlidersHost);

  function renderParamSliders(): void {
    paramSlidersHost.replaceChildren();
    for (const key of Object.keys(params)) {
      const handle = createSliderRow({
        id: `fam-${key}`,
        labelKey: key,
        i18n: ctx.i18n,
        min: -5,
        max: 5,
        step: 0.1,
        value: params[key] ?? 0,
        format: (v) => v.toFixed(2),
        onChange: (v) => {
          params[key] = v;
          redraw();
        },
      });
      paramSlidersHost.appendChild(handle.element);
    }
  }
  renderParamSliders();

  redraw();
  viewport = createCartesianViewport(svg, {
    base: { xDomain: [-10, 10], yDomain: [-10, 10] },
    plotRect: { x: 0, y: 0, width: 800, height: 460 },
    onChange(next) {
      view = next;
      redraw();
    },
  });

  createActionBar(
    ctx.actionBar,
    [
      {
        id: 'reset',
        labelKey: ctx.i18n.t('btn.reset'),
        onClick: () => {
          params = { ...FAMILY_PRESETS[family] };
          renderParamSliders();
          redraw();
        },
      },
      {
        id: 'randomise',
        labelKey: ctx.i18n.t('btn.randomise'),
        onClick: () => {
          const keys = Object.keys(params);
          if (keys.length === 0) return;
          const arr = new Uint32Array(keys.length);
          crypto.getRandomValues(arr);
          keys.forEach((k, idx) => {
            const u = (arr[idx] ?? 0) / 0xffffffff;
            params[k] = -3 + 6 * u;
          });
          renderParamSliders();
          redraw();
        },
      },
    ],
    { viewReset: { labelKey: ctx.i18n.t('btn.reset-view'), onResetView: () => viewport?.reset() } },
  );

  return {
    dispose() {
      graphHandle?.destroy();
      viewport?.dispose();
      legend.destroy();
      svg.remove();
      ctx.panel.replaceChildren();
      ctx.actionBar.replaceChildren();
    },
  };
}
